import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useTransform, useMotionValue, useSpring, useMotionValueEvent } from 'framer-motion';

import dzireEtiosImg from '../assets/images/dzire-etios.jpg';
import innovaImg from '../assets/images/innova.jpg';
import crystaImg from '../assets/images/crysta.jpg';
import corollaImg from '../assets/images/corolla.jpg';
import tempo11Img from '../assets/images/tempo-11-seater.jpg';
import tempo18Img from '../assets/images/tempo-18-seater.jpg';
import tempoLuxuryImg from '../assets/images/tempo-luxury.jpg';
import hero from '../assets/images/hero.png';
import chennai1 from '../assets/images/chennai1.jpg';
import chennai2 from '../assets/images/chennai-street.jpeg';
import chennai3 from '../assets/images/chennai3.jpg';

// ─── Font injection ────────────────────────────────────────────────────────────
const injectLink = (href) => {
  if (!document.querySelector(`link[href="${href}"]`)) {
    const l = Object.assign(document.createElement('link'), { rel: 'stylesheet', href });
    document.head.appendChild(l);
  }
};
injectLink('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,300;12..96,400;12..96,500;12..96,600;12..96,700;12..96,800&family=Lora:ital,wght@0,400;0,500;0,600;1,400;1,500;1,600&display=swap');
injectLink('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

// ─── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  bg:      '#FAFAF7',
  bgWarm:  '#F2EDE4',
  bgCard:  '#FFFFFF',
  bgDark:  '#0E1525',
  text:    '#111827',
  soft:    '#6B7280',
  softer:  '#9CA3AF',
  accent:  '#FDDB1C',
  accentD: '#E8C900',
  border:  '#E8E2D8',
  borderL: '#F0EBE2',
  green:   '#10B981',
  blue:    '#2563EB',
  display: "'Lora', Georgia, serif",
  sans:    "'Bricolage Grotesque', system-ui, sans-serif",
};

const fmt3 = (n) => n.toString().padStart(3, '0');

// ─── Preload keyframes IMMEDIATELY on module parse ─────────────────────────────
// Browser starts fetching these before React even renders — zero wait for frame 0
const preloadKeyframes = (total = 240) => {
  if (typeof document === 'undefined') return;
  const step = Math.floor(total / 12);
  for (let i = 0; i < total; i += step) {
    const href = `/images/etios-sequence/ezgif-frame-${fmt3(i + 1)}.jpg`;
    if (!document.head.querySelector(`link[href="${href}"]`)) {
      const l = document.createElement('link');
      l.rel = 'preload';
      l.as = 'image';
      l.setAttribute('fetchpriority', 'high');
      l.href = href;
      document.head.appendChild(l);
    }
  }
};
preloadKeyframes(); // ← fires at module parse time

// ─── Priority order builder ────────────────────────────────────────────────────
const buildPriorityOrder = (total) => {
  const order = [];
  const step = Math.max(1, Math.floor(total / 12));
  for (let i = 0; i < total; i += step) order.push(i);
  for (let i = 0; i < total; i++) if (!order.includes(i)) order.push(i);
  return order;
};

// ─── Canvas ────────────────────────────────────────────────────────────────────
const EtiosScrollCanvas = ({ smoothProgress, totalFrames = 240 }) => {
  const canvasRef   = useRef(null);
  const imagesRef   = useRef(new Array(totalFrames).fill(null));
  const readyRef    = useRef(new Array(totalFrames).fill(false));
  const [isReady,   setIsReady]   = useState(false);
  const [loadPct,   setLoadPct]   = useState(0);
  const lastDrawRef = useRef(-1);
  const doneRef     = useRef(0);
  const isReadyRef  = useRef(false); // mirror for motion event (avoids stale closure)

  // ── Core draw function ───────────────────────────────────────────────────────
  const drawFrame = useCallback((p, canvasOverride) => {
    const canvas = canvasOverride || canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: false, willReadFrequently: false });
    if (!ctx) return;

    const clamped = Math.max(0, Math.min(0.9999, p));
    const idx = Math.floor(clamped * totalFrames);
    if (lastDrawRef.current === idx) return;

    // Find nearest loaded frame — never flash blank
    let target = idx;
    if (!readyRef.current[target]) {
      let lo = target - 1, hi = target + 1;
      while (lo >= 0 || hi < totalFrames) {
        if (lo >= 0 && readyRef.current[lo]) { target = lo; break; }
        if (hi < totalFrames && readyRef.current[hi]) { target = hi; break; }
        lo--; hi++;
      }
    }
    if (!readyRef.current[target]) return;
    lastDrawRef.current = idx;

    const img = imagesRef.current[target];
    const r   = canvas.getBoundingClientRect();
    const cw  = r.width  || canvas.width;
    const ch  = r.height || canvas.height;
    if (!cw || !ch) return;

    const ia = img.naturalWidth / img.naturalHeight;
    const ca = cw / ch;
    let dw, dh, dx, dy;
    if (ia > ca) {
      dh = ch; dw = img.naturalWidth * ch / img.naturalHeight;
      dx = (cw - dw) / 2; dy = 0;
    } else {
      dw = cw; dh = img.naturalHeight * cw / img.naturalWidth;
      dx = 0; dy = (ch - dh) / 2;
    }
    ctx.drawImage(img, dx, dy, dw, dh);
  }, [totalFrames]);

  // ── Canvas sizing via useLayoutEffect — correct size BEFORE first paint ──────
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const r   = canvas.getBoundingClientRect();
      if (!r.width || !r.height) return;
      canvas.width  = r.width  * dpr;
      canvas.height = r.height * dpr;
      const ctx = canvas.getContext('2d', { alpha: false });
      ctx.scale(dpr, dpr);
      lastDrawRef.current = -1; // force redraw at new size
      drawFrame(smoothProgress.get(), canvas);
    };
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();
    return () => ro.disconnect();
  }, [drawFrame, smoothProgress]);

  // ── Boot loader ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const order = buildPriorityOrder(totalFrames);
    let firstFrameReady = false;

    const markLoaded = (idx, img) => {
      imagesRef.current[idx] = img;
      readyRef.current[idx]  = true;
      doneRef.current++;
      setLoadPct(Math.round((doneRef.current / totalFrames) * 100));
      if (!firstFrameReady && readyRef.current[0]) {
        firstFrameReady = true;
        isReadyRef.current = true;
        drawFrame(0);      // paint frame 0 synchronously — no setState delay
        setIsReady(true);
      }
      // Keep painting as more frames load (fills in gaps)
      drawFrame(smoothProgress.get());
    };

    order.forEach((idx) => {
      const img = new Image();
      img.decoding = 'async';
      const src = `/images/etios-sequence/ezgif-frame-${fmt3(idx + 1)}.jpg`;

      if (idx === 0) {
        // Frame 0: decode() resolves when GPU-ready — synchronous first paint
        img.src = src;
        img.decode()
          .then(() => markLoaded(0, img))
          .catch(() => {
            // Fallback for browsers without decode()
            img.onload = () => markLoaded(0, img);
          });
      } else {
        img.onload = () => markLoaded(idx, img);
        img.src = src;
      }
    });
  }, [totalFrames]); // eslint-disable-line

  // ── Subscribe via useMotionValueEvent — NO rAF polling ──────────────────────
  // Fires only when the value actually changes, not every frame
  useMotionValueEvent(smoothProgress, 'change', (latest) => {
    if (isReadyRef.current) drawFrame(latest);
  });

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', background: T.bgDark }}>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%', height: '100%', display: 'block',
          imageRendering: 'auto',
          willChange: 'contents',
        }}
      />
      {/* Loading overlay — fades out the instant frame 0 is ready */}
      <motion.div
        animate={{ opacity: isReady ? 0 : 1 }}
        transition={{ duration: 0.3 }}
        style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          background: T.bgDark, gap: '18px',
          pointerEvents: isReady ? 'none' : 'auto',
        }}
      >
        <div style={{ fontFamily: T.sans, fontSize: '13px', color: '#6B7280', letterSpacing: '0.08em' }}>
          Loading 360° view… {loadPct}%
        </div>
        <div style={{ width: '200px', height: '2px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
          <motion.div
            style={{ height: '100%', background: T.accent, borderRadius: '2px', originX: 0 }}
            animate={{ width: `${loadPct}%` }}
            transition={{ duration: 0.1 }}
          />
        </div>
        <div style={{ fontFamily: T.sans, fontSize: '11px', color: 'rgba(255,255,255,0.2)', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
          {loadPct < 30 ? 'Warming up engines…' : loadPct < 70 ? 'Almost there…' : 'Ready to roll!'}
        </div>
      </motion.div>
    </div>
  );
};

// ─── HUD ──────────────────────────────────────────────────────────────────────
const EtiosHUD = ({ scrollYProgress }) => {
  const opacity = useTransform(scrollYProgress, [0, 0.18], [1, 0]);
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 10 }}>
      <motion.div style={{ opacity, position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            style={{ fontFamily: T.sans, fontSize: '11px', letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', marginBottom: '20px' }}
          >
            360° Interactive View
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.7 }}
            style={{ fontFamily: T.display, fontStyle: 'italic', fontSize: 'clamp(3rem, 10vw, 8rem)', fontWeight: 500, color: '#fff', margin: 0, lineHeight: 0.95, letterSpacing: '-2px', textShadow: '0 8px 60px rgba(0,0,0,0.8)' }}
          >
            Toyota <span style={{ color: T.accent, fontStyle: 'normal' }}>Etios</span>
          </motion.h2>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
            style={{ marginTop: '28px', fontFamily: T.sans, fontSize: '12px', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.22em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
          >
            <motion.span animate={{ x: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut' }}>↕</motion.span>
            Scroll to Rotate
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

// ─── ScrollySection ───────────────────────────────────────────────────────────
const ScrollySection = ({ scrollYProgress, smoothProgress, scrollProgress }) => {
  const [phase, setPhase] = useState('before');
  const spacerRef = useRef(null);

  useEffect(() => {
    const update = () => {
      if (!spacerRef.current) return;
      const r = spacerRef.current.getBoundingClientRect();
      if (r.top > 1)                         setPhase('before');
      else if (r.bottom > window.innerHeight) setPhase('active');
      else                                    setPhase('after');
    };
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
    return () => { window.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, []);

  const ps = (() => {
    const b = { left: 0, right: 0, height: '100vh', zIndex: 50 };
    if (phase === 'active') return { ...b, position: 'fixed',    top: 0 };
    if (phase === 'after')  return { ...b, position: 'absolute', bottom: 0 };
    return                         { ...b, position: 'absolute', top: 0, opacity: 0, pointerEvents: 'none' };
  })();

  return (
    <div ref={spacerRef} style={{ position: 'relative', height: '600vh', background: T.bgDark }}>
      <div style={ps}>
        <EtiosScrollCanvas smoothProgress={smoothProgress} />
        <EtiosHUD scrollYProgress={scrollYProgress} />

        {phase === 'active' && (
          <div style={{ position: 'absolute', bottom: '32px', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: '16px', pointerEvents: 'none', zIndex: 60 }}>
            <div style={{ width: '160px', height: '2px', background: 'rgba(255,255,255,0.10)', borderRadius: '1px', overflow: 'hidden' }}>
              <motion.div
                style={{ height: '100%', background: T.accent, borderRadius: '1px', originX: 0 }}
                animate={{ width: `${scrollProgress * 100}%` }}
                transition={{ duration: 0.05 }}
              />
            </div>
            <span style={{ fontFamily: T.sans, fontSize: '11px', color: 'rgba(255,255,255,0.35)', minWidth: '32px' }}>
              {Math.round(scrollProgress * 100)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Section Intro helper ─────────────────────────────────────────────────────
const SectionIntro = ({ tag, title, subtitle, center = false, light = false }) => (
  <div style={{ textAlign: center ? 'center' : 'left', marginBottom: '48px' }}>
    {tag && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: center ? 'center' : 'flex-start', marginBottom: '12px' }}>
        <span style={{ width: '22px', height: '2px', background: T.accent, borderRadius: '1px', display: 'block' }} />
        <span style={{ fontFamily: T.sans, fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: light ? 'rgba(255,255,255,0.45)' : T.soft }}>{tag}</span>
      </div>
    )}
    <h2 style={{ fontFamily: T.display, fontSize: 'clamp(1.9rem, 4vw, 2.8rem)', fontWeight: 500, color: light ? '#fff' : T.text, margin: '0 0 12px', lineHeight: 1.15, letterSpacing: '-0.4px' }}>
      {title}
    </h2>
    {subtitle && <p style={{ fontFamily: T.sans, fontSize: '15px', color: light ? 'rgba(255,255,255,0.5)' : T.soft, lineHeight: 1.75, margin: 0, maxWidth: center ? '480px' : '460px', marginLeft: center ? 'auto' : 0, marginRight: center ? 'auto' : 0 }}>{subtitle}</p>}
  </div>
);

// ─── Home ────────────────────────────────────────────────────────────────────
const Home = () => {
  const [activeTest,   setActiveTest]   = useState(0);
  const [mob,          setMob]          = useState(window.innerWidth <= 768);
  const [sm,           setSm]           = useState(window.innerWidth <= 480);
  const [scrollProg,   setScrollProg]   = useState(0);
  const [hoveredFleet, setHoveredFleet] = useState(null);

  const wrapRef  = useRef(null);
  const topRef   = useRef(0);
  const rafRef   = useRef(null);

  // Raw scroll motion value
  const scrollY = useMotionValue(0);

  // ⭐ Buttery spring — stiffness 520 = ~20ms lag vs old 280 = ~80ms lag
  const smoothProgress = useSpring(scrollY, {
    stiffness: 520,
    damping:   52,
    mass:      0.3,
    restDelta: 0.00001,
  });

  const getTop = (el) => { let t = 0; while (el) { t += el.offsetTop; el = el.offsetParent; } return t; };
  const snap   = () => { if (wrapRef.current) topRef.current = getTop(wrapRef.current); };

  useEffect(() => {
    const t = setTimeout(snap, 200);
    window.addEventListener('resize', snap);
    return () => { clearTimeout(t); window.removeEventListener('resize', snap); };
  }, []);

  useEffect(() => {
    const onS = () => {
      if (!wrapRef.current) return;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const raw = wrapRef.current;
        if (!raw) return;
        let p = (window.scrollY - topRef.current) / (raw.offsetHeight - window.innerHeight);
        p = Math.max(0, Math.min(1, p));
        setScrollProg(p);
        scrollY.set(p);
      });
    };
    window.addEventListener('scroll', onS, { passive: true });
    onS();
    return () => { window.removeEventListener('scroll', onS); if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [scrollY]);

  // Preload all non-canvas images immediately on mount
  useEffect(() => {
    const srcs = [hero, chennai1, chennai2, chennai3, dzireEtiosImg, innovaImg, crystaImg, corollaImg, tempo11Img, tempo18Img, tempoLuxuryImg];
    srcs.forEach((src) => { const i = new Image(); i.src = src; });
  }, []);

  useEffect(() => { document.title = "Saha Travels - Best Travel Agency in Chennai"; }, []);
  useEffect(() => {
    const r = () => { setMob(window.innerWidth <= 768); setSm(window.innerWidth <= 480); };
    window.addEventListener('resize', r); return () => window.removeEventListener('resize', r);
  }, []);
  useEffect(() => {
    const t = setInterval(() => setActiveTest(p => (p + 1) % testimonials.length), 5000);
    return () => clearInterval(t);
  }, []);

  const px   = mob ? '20px' : '60px';
  const maxW = '1280px';

  const testimonials = [
    { text: 'Best service in Chennai! Drivers are always on time and very professional. I use SAHA for all my travels!', author: 'Priya K.',  role: 'Regular Customer',  av: 'P' },
    { text: 'Used them for my wedding guest transportation. Perfect coordination and spotlessly clean cars!',            author: 'Rahul M.', role: 'Event Organizer',    av: 'R' },
    { text: 'The Innova Crysta is incredibly comfortable for long trips. Best travel partner in all of Chennai!',       author: 'Anjali S.',role: 'Travel Enthusiast',  av: 'A' },
  ];

  const vehicles = [
    { name: 'Dzire / Etios',   image: dzireEtiosImg, tag: '4 Seater',          capacity: 4,  price: '₹12/km' },
    { name: 'Innova',          image: innovaImg,      tag: '7 Seater',          capacity: 7,  price: '₹15/km' },
    { name: 'Crysta',          image: crystaImg,      tag: '7 Seater',          capacity: 7,  price: '₹18/km' },
    { name: 'Corolla',         image: corollaImg,     tag: '5 Seater',          capacity: 5,  price: '₹20/km' },
    { name: 'Tempo 11 Seater', image: tempo11Img,     tag: '11 Seater',         capacity: 11, price: '₹25/km' },
    { name: 'Tempo 18 Seater', image: tempo18Img,     tag: '18 Seater',         capacity: 18, price: '₹30/km' },
    { name: 'Tempo 11 Luxury', image: tempoLuxuryImg, tag: '11 Seater Luxury',  capacity: 11, price: '₹35/km' },
  ];

  const features = [
    { icon: 'fa-shield-halved',     title: 'SOS Safety',          desc: 'Real-time ride tracking and 24/7 emergency support for every trip across Chennai.' },
    { icon: 'fa-circle-check',      title: 'Zero Cancellations',  desc: 'Our drivers always show up — on time, every time. Reliability you can count on.' },
    { icon: 'fa-indian-rupee-sign', title: 'Transparent Pricing', desc: 'No hidden fees or surprises. Tolls and taxes included — always.' },
    { icon: 'fa-star',              title: '4.9★ Google Rating',  desc: '50,000+ happy customers across Tamil Nadu trust SAHA for every journey.' },
  ];

  return (
    <>
      {/* ══ NAV ══════════════════════════════════════════════════════════════ */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(250,250,247,0.94)', backdropFilter: 'blur(14px)',
        borderBottom: `1px solid ${T.border}`,
        padding: mob ? '14px 20px' : '15px 60px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        willChange: 'transform',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <div style={{ width: '30px', height: '30px', background: T.accent, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="fas fa-car" style={{ fontSize: '12px', color: T.text }} />
          </div>
          <span style={{ fontFamily: T.sans, fontWeight: 800, fontSize: '17px', color: T.text, letterSpacing: '-0.3px' }}>SAHA</span>
          {!mob && <span style={{ fontFamily: T.sans, fontSize: '13px', color: T.softer }}>Travels</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: mob ? '12px' : '28px' }}>
          {!mob && (['Fleet','About','Contact'].map(n => (
            <a key={n} href={`#${n.toLowerCase()}`} style={{ fontFamily: T.sans, fontSize: '13px', fontWeight: 500, color: T.soft, textDecoration: 'none', transition: 'color 0.2s' }}
              onMouseEnter={e => e.target.style.color = T.text}
              onMouseLeave={e => e.target.style.color = T.soft}
            >{n}</a>
          )))}
          <a href="tel:+918637658382" style={{
            fontFamily: T.sans, fontSize: '13px', fontWeight: 700,
            background: T.accent, color: T.text,
            padding: mob ? '9px 18px' : '10px 22px', borderRadius: '50px',
            textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '7px',
            boxShadow: '0 3px 14px rgba(253,219,28,0.4)',
            transition: 'transform 0.18s, box-shadow 0.18s',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(253,219,28,0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 3px 14px rgba(253,219,28,0.4)'; }}
          >
            <i className="fas fa-phone" style={{ fontSize: '11px' }} />{mob ? 'Book' : 'Book a Ride'}
          </a>
        </div>
      </nav>

      {/* ══ HERO ═════════════════════════════════════════════════════════════ */}
      <section style={{ minHeight: '100vh', background: T.bg, paddingTop: '78px', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'absolute', top: '-100px', right: mob ? '-120px' : '-80px', width: mob ? '380px' : '680px', height: mob ? '380px' : '680px', borderRadius: '50%', background: T.accent, opacity: 0.11, filter: 'blur(100px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '0', left: '-80px', width: '360px', height: '360px', borderRadius: '50%', background: T.blue, opacity: 0.06, filter: 'blur(80px)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: maxW, margin: '0 auto', width: '100%', padding: mob ? '40px 20px 60px' : '40px 60px 60px', display: 'grid', gridTemplateColumns: mob ? '1fr' : '1.15fr 0.85fr', gap: mob ? '40px' : '20px', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: '50px', padding: '7px 14px 7px 9px', marginBottom: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: T.green, display: 'block', boxShadow: `0 0 0 3px rgba(16,185,129,0.2)`, animation: 'pulse-green 2s infinite' }} />
              <span style={{ fontFamily: T.sans, fontSize: '12px', fontWeight: 600, color: T.text }}>Chennai's #1 Travel Partner · Since 2019</span>
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
              style={{ fontFamily: T.display, fontSize: mob ? '40px' : sm ? '34px' : 'clamp(3rem, 5.5vw, 4.8rem)', fontWeight: 600, lineHeight: 1.07, color: T.text, margin: '0 0 18px', letterSpacing: '-1.5px' }}>
              Premium Rides,<br />
              <em style={{ fontStyle: 'italic', fontWeight: 400 }}>Anywhere</em>{' '}
              <span style={{ position: 'relative', display: 'inline-block' }}>
                in Chennai
                <span style={{ position: 'absolute', bottom: '2px', left: 0, width: '100%', height: '9px', background: T.accent, zIndex: -1, opacity: 0.45, borderRadius: '3px' }} />
              </span>
            </motion.h1>

            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
              style={{ fontFamily: T.sans, fontSize: '15.5px', color: T.soft, lineHeight: 1.76, margin: '0 0 32px', maxWidth: '480px' }}>
              Safe, comfortable, and always on time. Professional drivers, clear pricing, and zero cancellations — across Chennai and Tamil Nadu.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '40px' }}>
              <a href="tel:+918637658382" style={{ fontFamily: T.sans, fontSize: '14px', fontWeight: 700, background: T.accent, color: T.text, padding: '14px 28px', borderRadius: '50px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 20px rgba(253,219,28,0.38)', transition: 'transform 0.18s, box-shadow 0.18s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(253,219,28,0.5)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(253,219,28,0.38)'; }}>
                <i className="fas fa-phone" style={{ fontSize: '12px' }} /> Book a Ride Now
              </a>
              <a href="#fleet" style={{ fontFamily: T.sans, fontSize: '14px', fontWeight: 600, background: 'transparent', color: T.text, padding: '14px 24px', borderRadius: '50px', textDecoration: 'none', border: `1.5px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: '8px', transition: 'border-color 0.2s, background 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.accentD; e.currentTarget.style.background = 'rgba(253,219,28,0.07)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = 'transparent'; }}>
                View Fleet <i className="fas fa-arrow-right" style={{ fontSize: '11px' }} />
              </a>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.44 }}
              style={{ display: 'flex', gap: mob ? '20px' : '36px', flexWrap: 'wrap', paddingTop: '28px', borderTop: `1px solid ${T.borderL}` }}>
              {[['2019','Founded'],['180+','Vehicles'],['50k+','Riders'],['4.9★','Rating']].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: T.sans, fontSize: mob ? '21px' : '26px', fontWeight: 800, color: T.text, letterSpacing: '-0.5px', lineHeight: 1 }}>{v}</div>
                  <div style={{ fontFamily: T.sans, fontSize: '11px', color: T.soft, marginTop: '4px' }}>{l}</div>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.18 }}
            style={{ position: 'relative', display: 'flex', justifyContent: mob ? 'center' : 'flex-end', alignItems: 'center' }}>
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: mob ? '260px' : '420px', height: mob ? '260px' : '420px', borderRadius: '50%', background: T.accent, opacity: 0.13 }} />
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: mob ? '200px' : '330px', height: mob ? '200px' : '330px', border: `1.5px dashed ${T.accent}`, borderRadius: '50%', opacity: 0.2, animation: 'spin-slow 30s linear infinite' }} />
            <img src={hero} alt="SAHA Luxury Car" style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: mob ? '300px' : '510px', objectFit: 'contain', filter: 'drop-shadow(0 24px 60px rgba(0,0,0,0.13))' }} />

            <div style={{ position: 'absolute', bottom: mob ? '0' : '20px', left: mob ? '0' : '-10px', zIndex: 2, background: T.bgCard, borderRadius: '16px', padding: '12px 18px', boxShadow: '0 8px 32px rgba(0,0,0,0.1)', border: `1px solid ${T.borderL}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '38px', height: '38px', background: T.accent, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="fas fa-car" style={{ fontSize: '14px', color: T.text }} />
              </div>
              <div>
                <div style={{ fontFamily: T.sans, fontWeight: 700, fontSize: '13px', color: T.text }}>180+ Vehicles</div>
                <div style={{ fontFamily: T.sans, fontSize: '11px', color: T.soft, marginTop: '1px' }}>Across Chennai</div>
              </div>
            </div>

            <div style={{ position: 'absolute', top: mob ? '0' : '20px', right: mob ? '0' : '-14px', zIndex: 2, background: T.bgDark, borderRadius: '14px', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.22)' }}>
              <i className="fas fa-star" style={{ color: T.accent, fontSize: '12px' }} />
              <span style={{ fontFamily: T.sans, fontSize: '13px', fontWeight: 700, color: '#fff' }}>4.9 Google Rating</span>
            </div>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}
          style={{ position: 'absolute', bottom: '28px', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', zIndex: 2 }}>
          <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}
            style={{ width: '1px', height: '44px', background: `linear-gradient(to bottom, ${T.border}, transparent)` }} />
          <span style={{ fontFamily: T.sans, fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', color: T.softer }}>Scroll</span>
        </motion.div>
      </section>

      {/* ══ MARQUEE STRIP ════════════════════════════════════════════════════ */}
      <div style={{ background: T.accent, overflow: 'hidden', padding: '13px 0', borderTop: `1px solid ${T.accentD}`, borderBottom: `1px solid ${T.accentD}` }}>
        <div style={{ display: 'flex', animation: 'marquee 22s linear infinite', whiteSpace: 'nowrap' }}>
          {Array(3).fill(['✦ Safe Rides','✦ On-Time Pickups','✦ 180+ Vehicles','✦ Zero Cancellations','✦ Clear Pricing','✦ Chennai Experts Since 2019','✦ 50,000+ Happy Customers','✦ 4.9★ Rating']).flat().map((t, i) => (
            <span key={i} style={{ fontFamily: T.sans, fontSize: '13px', fontWeight: 700, color: T.text, padding: '0 28px', letterSpacing: '0.02em' }}>{t}</span>
          ))}
        </div>
      </div>

      {/* ══ TESTIMONIALS ═════════════════════════════════════════════════════ */}
      <section style={{ background: T.bgWarm, padding: mob ? '68px 20px' : '96px 60px' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <SectionIntro tag="Customer Reviews" title={<>What Chennai Says<br /><em style={{ fontStyle: 'italic', fontWeight: 400 }}>About Us</em></>} />
          <div style={{ maxWidth: '680px' }}>
            <AnimatePresence mode="wait">
              <motion.div key={activeTest}
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.38 }}
                style={{ background: T.bgCard, borderRadius: '20px', padding: mob ? '24px' : '36px', border: `1.5px solid ${T.border}`, boxShadow: '0 4px 24px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', gap: '4px', marginBottom: '18px' }}>
                  {Array(5).fill(0).map((_, i) => <i key={i} className="fas fa-star" style={{ color: T.accent, fontSize: '13px' }} />)}
                </div>
                <p style={{ fontFamily: T.display, fontSize: mob ? '18px' : '22px', fontStyle: 'italic', color: T.text, lineHeight: 1.55, margin: '0 0 24px' }}>
                  "{testimonials[activeTest].text}"
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: T.bgDark, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontFamily: T.sans, fontWeight: 800, fontSize: '15px', color: T.accent }}>{testimonials[activeTest].av}</span>
                  </div>
                  <div>
                    <div style={{ fontFamily: T.sans, fontWeight: 700, fontSize: '14px', color: T.text }}>{testimonials[activeTest].author}</div>
                    <div style={{ fontFamily: T.sans, fontSize: '12px', color: T.soft, marginTop: '2px' }}>{testimonials[activeTest].role}</div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              {testimonials.map((_, i) => (
                <button key={i} onClick={() => setActiveTest(i)} style={{ width: i === activeTest ? '28px' : '8px', height: '8px', borderRadius: '4px', background: i === activeTest ? T.accent : T.border, border: 'none', cursor: 'pointer', transition: 'all 0.3s', padding: 0 }} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ 360° INTRO + SCROLLY ═════════════════════════════════════════════ */}
      <section style={{ background: T.bg, padding: mob ? '68px 20px 0' : '96px 60px 0' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '20px', paddingBottom: mob ? '32px' : '48px' }}>
            <SectionIntro tag="360° Experience" title={<>Explore the<br /><span style={{ color: T.blue }}>Toyota Etios</span></>} subtitle="Scroll through a complete 360° view of our most popular vehicle." />
            <div style={{ fontFamily: T.sans, fontSize: '13px', color: T.soft, display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: mob ? '0' : '48px' }}>
              <i className="fas fa-sync-alt" style={{ color: T.accent }} /> Scroll to rotate
            </div>
          </div>
        </div>
      </section>

      <div ref={wrapRef}>
        <ScrollySection
          scrollYProgress={scrollY}
          smoothProgress={smoothProgress}
          scrollProgress={scrollProg}
        />
      </div>

      {/* ══ FLEET ════════════════════════════════════════════════════════════ */}
      <section id="fleet" style={{ background: T.bg, padding: mob ? '68px 20px' : '96px 60px' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <SectionIntro tag="Our Premium Fleet" title={<>7 Vehicles for<br /><em style={{ fontStyle: 'italic', fontWeight: 400 }}>Every Journey</em></>} subtitle="From solo rides to group travel — the perfect vehicle for every trip." />
          <div style={{ display: 'grid', gridTemplateColumns: sm ? '1fr' : mob ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: mob ? '16px' : '24px' }}>
            {vehicles.map((v, i) => {
              const active = hoveredFleet === i;
              return (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  viewport={{ once: true, margin: '-50px' }}
                  onMouseEnter={() => setHoveredFleet(i)}
                  onMouseLeave={() => setHoveredFleet(null)}
                  style={{
                    background: active ? T.bgDark : T.bgCard,
                    borderRadius: '20px', overflow: 'hidden', cursor: 'pointer',
                    transition: 'all 0.35s cubic-bezier(0.2, 0.9, 0.4, 1)',
                    boxShadow: active ? '0 20px 40px rgba(14,21,37,0.25)' : '0 4px 12px rgba(0,0,0,0.04)',
                    transform: active ? 'translateY(-8px)' : 'translateY(0)',
                    border: `1.5px solid ${active ? T.accent : T.border}`,
                    willChange: 'transform',
                  }}>
                  <div style={{ background: '#FFFFFF', padding: mob ? '20px' : '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: `1px solid ${active ? 'rgba(255,255,255,0.1)' : T.border}` }}>
                    <img src={v.image} alt={v.name} loading="lazy" style={{ width: '100%', height: mob ? '80px' : '100px', objectFit: 'contain', transition: 'transform 0.4s ease', transform: active ? 'scale(1.08)' : 'scale(1)' }} />
                  </div>
                  <div style={{ padding: mob ? '16px' : '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <h3 style={{ fontFamily: T.sans, fontWeight: 700, fontSize: mob ? '14px' : '16px', color: active ? '#fff' : T.text, margin: 0, lineHeight: 1.3 }}>{v.name}</h3>
                        <div style={{ display: 'inline-block', background: active ? T.accent : 'rgba(253,219,28,0.12)', padding: '4px 10px', borderRadius: '20px', marginTop: '8px' }}>
                          <span style={{ fontFamily: T.sans, fontSize: '10px', fontWeight: 600, color: active ? T.bgDark : T.accent }}>{v.tag}</span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontFamily: T.sans, fontSize: '18px', fontWeight: 800, color: active ? T.accent : T.accentD }}>{v.price}</div>
                        <div style={{ fontFamily: T.sans, fontSize: '10px', color: active ? 'rgba(255,255,255,0.4)' : T.soft }}>starting from</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', marginTop: '16px', paddingTop: '12px', borderTop: `1px solid ${active ? 'rgba(255,255,255,0.1)' : T.border}` }}>
                      {[['fa-user', `${v.capacity} Seats`], ['fa-snowflake', 'AC'], ['fa-suitcase', 'Luggage']].map(([icon, label]) => (
                        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <i className={`fas ${icon}`} style={{ fontSize: '11px', color: active ? T.accent : T.soft }} />
                          <span style={{ fontFamily: T.sans, fontSize: '11px', color: active ? 'rgba(255,255,255,0.6)' : T.soft }}>{label}</span>
                        </div>
                      ))}
                    </div>
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: active ? 1 : 0, y: active ? 0 : 10 }} transition={{ duration: 0.25 }} style={{ marginTop: '16px' }}>
                      <button onClick={(e) => { e.stopPropagation(); window.location.href = 'tel:+918637658382'; }}
                        style={{ width: '100%', background: T.accent, border: 'none', padding: '10px', borderRadius: '12px', fontFamily: T.sans, fontSize: '12px', fontWeight: 700, color: T.bgDark, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'opacity 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                        <i className="fas fa-phone" style={{ fontSize: '10px' }} /> Book Now
                      </button>
                    </motion.div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══ ABOUT ════════════════════════════════════════════════════════════ */}
      <section id="about" style={{ background: T.bgWarm, padding: mob ? '68px 20px' : '96px 60px' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: mob ? '40px' : '80px', alignItems: 'center' }}>
            <motion.div initial={{ opacity: 0, x: -18 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.65 }} viewport={{ once: true }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <img src={chennai1} alt="" loading="eager" style={{ gridColumn: 'span 2', width: '100%', height: mob ? '180px' : '230px', objectFit: 'cover', borderRadius: '14px' }} />
                <img src={chennai2} alt="" loading="eager" style={{ width: '100%', height: mob ? '120px' : '155px', objectFit: 'cover', borderRadius: '10px' }} />
                <img src={chennai3} alt="" loading="eager" style={{ width: '100%', height: mob ? '120px' : '155px', objectFit: 'cover', borderRadius: '10px' }} />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 18 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.65 }} viewport={{ once: true }}>
              <SectionIntro tag="Our Story" title={<>Vanakkam,<br />We're <em style={{ fontStyle: 'italic', fontWeight: 400, color: T.blue }}>SAHA Travels</em></>} />
              <p style={{ fontFamily: T.sans, fontSize: '15px', color: T.soft, lineHeight: 1.8, marginBottom: '16px' }}>
                Founded in Chennai in 2019, SAHA Travels has grown from a single car to a family of <strong style={{ color: T.text }}>180+ vehicles</strong> across Tamil Nadu.
              </p>
              <p style={{ fontFamily: T.sans, fontSize: '15px', color: T.soft, lineHeight: 1.8, marginBottom: '32px' }}>
                Our drivers are local Chennai experts who know every road — from the IT corridor to the narrow lanes of Mylapore. Every ride comes with a smile and full professionalism.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '24px', paddingTop: '28px', borderTop: `1px solid ${T.border}` }}>
                {[['2019','Est. Year'],['180+','Vehicles'],['50k+','Customers']].map(([v, l]) => (
                  <div key={l}>
                    <div style={{ fontFamily: T.sans, fontSize: mob ? '22px' : '28px', fontWeight: 800, color: T.text, lineHeight: 1, letterSpacing: '-0.5px' }}>{v}</div>
                    <div style={{ fontFamily: T.sans, fontSize: '11px', color: T.soft, marginTop: '5px' }}>{l}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="fas fa-heart" style={{ color: '#E63946', fontSize: '12px' }} />
                <span style={{ fontFamily: T.sans, fontSize: '13px', fontWeight: 600, color: T.text }}>Proudly Made in Chennai, Always for You</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ WHY SAHA ═════════════════════════════════════════════════════════ */}
      <section style={{ background: T.bg, padding: mob ? '68px 20px' : '96px 60px' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <SectionIntro tag="Why Choose SAHA" title={<>Built for Chennai.<br /><em style={{ fontStyle: 'italic', fontWeight: 400 }}>Trusted by Thousands.</em></>} subtitle="Every feature is designed around your comfort and Chennai's roads." />
          <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr 1fr' : 'repeat(4,1fr)', gap: mob ? '12px' : '16px' }}>
            {features.map((f, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: i * 0.09 }} viewport={{ once: true }}
                style={{ background: T.bgCard, border: `1.5px solid ${T.border}`, borderRadius: '16px', padding: mob ? '18px' : '24px', transition: 'all 0.25s ease', willChange: 'transform' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.boxShadow = `0 8px 28px rgba(253,219,28,0.14)`; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                <div style={{ width: '42px', height: '42px', background: T.bgWarm, borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px', border: `1px solid ${T.border}` }}>
                  <i className={`fas ${f.icon}`} style={{ fontSize: '16px', color: T.text }} />
                </div>
                <h3 style={{ fontFamily: T.sans, fontSize: mob ? '13px' : '14px', fontWeight: 700, color: T.text, margin: '0 0 7px', lineHeight: 1.3 }}>{f.title}</h3>
                <p style={{ fontFamily: T.sans, fontSize: '12px', color: T.soft, lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA BANNER ═══════════════════════════════════════════════════════ */}
      <section id="contact" style={{ background: T.bgDark, padding: mob ? '68px 20px' : '96px 60px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '380px', height: '380px', borderRadius: '50%', background: T.accent, opacity: 0.07, filter: 'blur(80px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '300px', height: '300px', borderRadius: '50%', background: T.blue, opacity: 0.08, filter: 'blur(70px)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: maxW, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr auto', gap: mob ? '36px' : '60px', alignItems: 'center' }}>
            <div>
              <SectionIntro light tag="Ready to Go?" title={<>Book Your Ride<br /><em style={{ fontStyle: 'italic', color: T.accent }}>Today.</em></>} subtitle="Call us or WhatsApp — we're available 24/7 across Chennai and Tamil Nadu." />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flexShrink: 0 }}>
              <a href="tel:+918637658382" style={{ fontFamily: T.sans, fontSize: '14px', fontWeight: 700, background: T.accent, color: T.text, padding: '16px 32px', borderRadius: '50px', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 4px 22px rgba(253,219,28,0.28)', whiteSpace: 'nowrap', transition: 'transform 0.18s, box-shadow 0.18s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(253,219,28,0.42)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 22px rgba(253,219,28,0.28)'; }}>
                <i className="fas fa-phone" /> Call to Book
              </a>
              <a href="https://wa.me/918637658382?text=Hi%20Saha%20Travels%2C%20I%20want%20to%20book%20a%20ride" style={{ fontFamily: T.sans, fontSize: '14px', fontWeight: 600, background: 'rgba(255,255,255,0.07)', color: '#fff', padding: '16px 32px', borderRadius: '50px', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', border: '1.5px solid rgba(255,255,255,0.14)', whiteSpace: 'nowrap', transition: 'background 0.2s, border-color 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(37,211,102,0.12)'; e.currentTarget.style.borderColor = 'rgba(37,211,102,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.14)'; }}>
                <i className="fab fa-whatsapp" style={{ color: '#25D366', fontSize: '15px' }} /> WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ══ GLOBAL STYLES ════════════════════════════════════════════════════ */}
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body {
          background: #FAFAF7;
          font-family: 'Bricolage Grotesque', system-ui, sans-serif;
          overflow-x: hidden;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        img { max-width: 100%; display: block; }
        a { transition: opacity 0.18s; }
        ::selection { background: #FDDB1C; color: #111827; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #F2EDE4; }
        ::-webkit-scrollbar-thumb { background: #C9C0B0; border-radius: 2px; }
        ::-webkit-scrollbar-thumb:hover { background: #FDDB1C; }

        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        @keyframes spin-slow {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to   { transform: translate(-50%, -50%) rotate(360deg); }
        }
        @keyframes pulse-green {
          0%, 100% { box-shadow: 0 0 0 2px rgba(16,185,129,0.25); }
          50%       { box-shadow: 0 0 0 5px rgba(16,185,129,0.1); }
        }
      `}</style>
    </>
  );
};

export default Home;